// dependencies
import { cva } from "class-variance-authority";
import React, { forwardRef, useState } from "react";
import { BaseTopColors } from "theme";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

// utils
import { cn } from "../../../lib/utils";
import { getIconSize } from "./utils";

// components
import { Icon } from "../Icon";

const buttonIconVariants = cva("inline-flex items-center justify-center rounded-1 font-semibold hover:cursor-pointer", {
	variants: {
		variant: {
			standard: "border-none bg-transparent p-0",
			outline: "border border-solid bg-transparent p-0",
			ghost: "rounded-full border-none bg-transparent",
		},
		color: {
			primary: "",
			neutral: "",
			red: "",
			green: "",
			yellow: "",
		},
		shade: {
			0: "",
			3: "",
			5: "",
			7: "",
		},
		size: {
			12: "size-3", // 12x12
			16: "size-4", // 16x16
			20: "size-5", // 20x20
			24: "size-6", // 24x24
			32: "size-8", // 32x32
			36: "size-9", // 36x36
			40: "size-10", // 40x40
		},
		disabled: {
			true: "cursor-not-allowed opacity-[38%] hover:cursor-not-allowed",
		},
	},
	compoundVariants: [
		// Standard variants - very light shade
		{
			variant: "standard",
			color: "neutral",
			shade: 0,
			className: "text-neutral-0 hover:text-neutral-3 active:text-neutral-3",
		},
		{
			variant: "standard",
			color: "primary",
			shade: 0,
			className: "text-primary-0 hover:text-primary-3 active:text-primary-3",
		},
		{
			variant: "standard",
			color: "green",
			shade: 0,
			className: "text-green-0 hover:text-green-3 active:text-green-3",
		},
		{
			variant: "standard",
			color: "red",
			shade: 0,
			className: "text-red-0 hover:text-red-3 active:text-red-3",
		},
		{
			variant: "standard",
			color: "yellow",
			shade: 0,
			className: "text-yellow-0 hover:text-yellow-3 active:text-yellow-3",
		},
		// Standard variants - light shade
		{
			variant: "standard",
			color: "neutral",
			shade: 3,
			className: "text-neutral-3 hover:text-neutral-5 active:text-neutral-5",
		},
		{
			variant: "standard",
			color: "primary",
			shade: 3,
			className: "text-primary-3 hover:text-primary-5 active:text-primary-5",
		},
		{
			variant: "standard",
			color: "green",
			shade: 3,
			className: "text-green-3 hover:text-green-5 active:text-green-5",
		},
		{
			variant: "standard",
			color: "red",
			shade: 3,
			className: "text-red-3 hover:text-red-5 active:text-red-5",
		},
		{
			variant: "standard",
			color: "yellow",
			shade: 3,
			className: "text-yellow-3 hover:text-yellow-5 active:text-yellow-5",
		},
		// Standard variants - regular shade
		{
			variant: "standard",
			color: "neutral",
			shade: 5,
			className: "text-neutral-5 hover:text-neutral-7 active:text-neutral-7",
		},
		{
			variant: "standard",
			color: "primary",
			shade: 5,
			className: "text-primary-5 hover:text-primary-7 active:text-primary-7",
		},
		{
			variant: "standard",
			color: "green",
			shade: 5,
			className: "text-green-5 hover:text-green-7 active:text-green-7",
		},
		{
			variant: "standard",
			color: "red",
			shade: 5,
			className: "text-red-5 hover:text-red-7 active:text-red-7",
		},
		{
			variant: "standard",
			color: "yellow",
			shade: 5,
			className: "text-yellow-5 hover:text-yellow-7 active:text-yellow-7",
		},
		// Standard variants - dark shade
		{
			variant: "standard",
			color: "neutral",
			shade: 7,
			className: "text-neutral-7 hover:text-neutral-9 active:text-neutral-9",
		},
		{
			variant: "standard",
			color: "primary",
			shade: 7,
			className: "text-primary-7 hover:text-primary-9 active:text-primary-9",
		},
		{
			variant: "standard",
			color: "green",
			shade: 7,
			className: "text-green-7 hover:text-green-9 active:text-green-9",
		},
		{
			variant: "standard",
			color: "red",
			shade: 7,
			className: "text-red-7 hover:text-red-9 active:text-red-9",
		},
		{
			variant: "standard",
			color: "yellow",
			shade: 7,
			className: "text-yellow-7 hover:text-yellow-9 active:text-yellow-9",
		},
		// Outline variants
		{
			variant: "outline",
			color: "neutral",
			className: "border-neutral-3 text-neutral-7 hover:bg-neutral-2",
		},
		{
			variant: "outline",
			color: "primary",
			className: "border-primary-3 text-primary-7 hover:bg-primary-2",
		},
		{
			variant: "outline",
			color: "green",
			className: "border-green-3 text-green-7 hover:bg-green-2",
		},
		{
			variant: "outline",
			color: "red",
			className: "border-red-3 text-red-7 hover:bg-red-2",
		},
		{
			variant: "outline",
			color: "yellow",
			className: "border-yellow-3 text-yellow-7 hover:bg-yellow-2",
		},
		// Ghost variants
		{
			variant: "ghost",
			color: "neutral",
			className: "text-neutral-7 hover:bg-neutral-2 active:bg-neutral-7 active:text-neutral-0",
		},
		{
			variant: "ghost",
			color: "primary",
			className: "text-primary-7 hover:bg-primary-2 active:bg-primary-7 active:text-neutral-0",
		},
		{
			variant: "ghost",
			color: "primary",
			shade: 3,
			className:
				"text-primary-3 hover:bg-primary-2 hover:text-primary-5 active:bg-primary-3 active:text-primary-7",
		},
		{
			variant: "ghost",
			color: "primary",
			shade: 5,
			className:
				"text-primary-5 hover:bg-primary-3 hover:text-primary-7 active:bg-primary-3 active:text-primary-7",
		},
		{
			variant: "ghost",
			color: "green",
			className: "text-green-7 hover:bg-green-2 active:bg-green-7 active:text-neutral-0",
		},
		{
			variant: "ghost",
			color: "red",
			className: "text-red-7 hover:bg-red-2 active:bg-red-7 active:text-neutral-0",
		},
		{
			variant: "ghost",
			color: "yellow",
			className: "text-yellow-7 hover:bg-yellow-2 active:bg-yellow-7 active:text-neutral-0",
		},
		{
			variant: "outline",
			disabled: true,
			className: "hover:bg-transparent",
		},
		{
			variant: "ghost",
			disabled: true,
			className: "hover:bg-transparent",
		},
	],
	defaultVariants: {
		variant: "standard",
		color: "neutral",
		shade: 5,
		size: 24,
		disabled: false,
	},
});

export interface ButtonIconProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color" | "aria-label"> {
	/**
	 * Accessible label for screen readers. Required for icon-only buttons.
	 */
	"aria-label": string;
	/**
	 * The variant of the button
	 */
	variant?: "standard" | "outline" | "ghost";
	/**
	 * The color of the button
	 */
	color?: BaseTopColors;
	/**
	 * The shade of the button. 3 is light, 5 is regular, 7 is dark
	 */
	shade?: 0 | 3 | 5 | 7;
	/**
	 * The size of the button
	 */
	size?: 12 | 16 | 20 | 24 | 32 | 36 | 40;
	/**
	 * If true, the button will be disabled
	 */
	disabled?: boolean;
	/**
	 * If true, the icon will rotate 180 degrees on click
	 */
	rotatingIcon?: boolean;
	/**
	 * The FontAwesome icon definition
	 */
	icon: IconDefinition;
	/**
	 * Optional class name to apply to the button
	 */
	dangerouslySetClassName?: string;
	/**
	 * The function to call when the button is clicked
	 */
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
	/**
	 * Optional URL to open in a new tab when clicked
	 */
	href?: string;
}

/**
 * The ButtonIcon component is a button that displays an icon only.
 */
const ButtonIcon = forwardRef<HTMLButtonElement, ButtonIconProps>((props, ref) => {
	const {
		variant = "standard",
		color = "neutral",
		shade = 5,
		size = 24,
		disabled = false,
		rotatingIcon = false,
		icon,
		dangerouslySetClassName,
		onClick,
		href,
		className,
		...restProps
	} = props;

	const iconSize = getIconSize(variant, size);

	const [isActive, setIsActive] = useState(false);

	return (
		<button
			ref={ref}
			className={cn(
				buttonIconVariants({ variant, color, shade, size, disabled }),
				dangerouslySetClassName,
				className
			)}
			disabled={disabled}
			type="button"
			onClick={(e) => {
				if (rotatingIcon) {
					setIsActive(!isActive);
				}
				if (href) {
					window.open(href, "_blank", "noopener,noreferrer");
				}
				onClick?.(e);
			}}
			{...restProps}
		>
			<Icon
				icon={icon}
				color={null}
				size={iconSize}
				dangerouslySetClassName={
					isActive && rotatingIcon ? "rotate-180 transition duration-300" : "rotate-0 transition duration-300"
				}
			/>
		</button>
	);
});

ButtonIcon.displayName = "ButtonIcon";

export { ButtonIcon, buttonIconVariants };
